import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    FacilityAllianceMemberBreadcrumbs,
    FacilityAllianceMemberCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsInstitutionsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as FacilityAllianceMembersActions from "../../../actions/facilityAllianceMembers";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { InstitutionLookupsLayout } from "../../index";

import "./FacilityAllianceMemberPage.scss";

interface FacilityAllianceMembersPageProps {
    form: Dtos.FacilityAllianceMember & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    facilityAllianceMember: Dtos.FacilityAllianceMember;
    lookups: Dtos.Lookup[];
    loadingFacilityAllianceMembers: boolean;
    loadFacilityAllianceMembersFailure: boolean;
    loadFacilityAllianceMembersErrorCode: string;
    savingFacilityAllianceMembers: boolean;
    saveFacilityAllianceMembersSuccess: boolean;
    saveFacilityAllianceMembersFailure: boolean;
    creatingFacilityAllianceMembers: boolean;
    createFacilityAllianceMembersSuccess: boolean;
    validatingFacilityAllianceMember: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearFacilityAllianceMembers: FacilityAllianceMembersActions.IFacilityAllianceMemberClearActionCreator;
    loadFacilityAllianceMembers: FacilityAllianceMembersActions.IFacilityAllianceMemberLoadByIdActionCreator;
    saveFacilityAllianceMembers: FacilityAllianceMembersActions.IFacilityAllianceMemberSaveActionCreator;
    createFacilityAllianceMembers: FacilityAllianceMembersActions.IFacilityAllianceMemberCreateActionCreator;
    validateFacilityAllianceMember: FacilityAllianceMembersActions.IFacilityAllianceMemberValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.facilityAllianceMember";

const FacilityAllianceMembersPage: FC<FacilityAllianceMembersPageProps> = ({
    form,
    formState,
    formProperties,
    facilityAllianceMember,
    lookups,
    loadingFacilityAllianceMembers,
    loadFacilityAllianceMembersFailure,
    loadFacilityAllianceMembersErrorCode,
    savingFacilityAllianceMembers,
    saveFacilityAllianceMembersSuccess,
    saveFacilityAllianceMembersFailure,
    creatingFacilityAllianceMembers,
    createFacilityAllianceMembersSuccess,
    validationFailures,
    permissions,
    validatingFacilityAllianceMember,
    clearFacilityAllianceMembers,
    loadFacilityAllianceMembers,
    saveFacilityAllianceMembers,
    createFacilityAllianceMembers,
    validateFacilityAllianceMember,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { facilityAllianceMemberId } = useParams<{
        facilityAllianceMemberId: string;
    }>();
    const facilityAllianceMemberIdNumber = parseInt(
        facilityAllianceMemberId,
        10
    );

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.FacilityAllianceMember =
            {} as Dtos.FacilityAllianceMember;

        loadForm(
            reduxFormName,
            Object.assign({ ...facilityAllianceMember }, extraData)
        );
    };


    // Load facility alliance member
    useEffect(() => {
        if (
            facilityAllianceMemberIdNumber &&
            !facilityAllianceMember &&
            !loadingFacilityAllianceMembers
        ) {
            setDrawerItems(SettingsInstitutionsSubMenu(permissions));
            setBreadcrumbs(
                FacilityAllianceMemberBreadcrumbs(
                    facilityAllianceMemberIdNumber
                )
            );
            loadFacilityAllianceMembers(facilityAllianceMemberIdNumber);
        } else if (
            !facilityAllianceMemberIdNumber &&
            !creatingFacilityAllianceMembers &&
            !createFacilityAllianceMembersSuccess
        ) {
            createFacilityAllianceMembers();
            setBreadcrumbs(FacilityAllianceMemberCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (facilityAllianceMember && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        facilityAllianceMemberIdNumber,
        facilityAllianceMember,
        setDrawerItems,
        SettingsInstitutionsSubMenu,
        permissions,
        setBreadcrumbs,
        FacilityAllianceMemberBreadcrumbs,
        loadFacilityAllianceMembers,
        createFacilityAllianceMembers,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingFacilityAllianceMembers,
        creatingFacilityAllianceMembers,
        createFacilityAllianceMembersSuccess,
        FacilityAllianceMemberCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadFacilityAllianceMembersFailure) {
            navigate(`/error/${loadFacilityAllianceMembersErrorCode}`);
        }
    }, [
        loadFacilityAllianceMembersFailure,
        loadFacilityAllianceMembersErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (facilityAllianceMemberIdNumber && facilityAllianceMember?.value) {
            setBreadcrumbs(
                FacilityAllianceMemberBreadcrumbs(
                    facilityAllianceMemberIdNumber,
                    facilityAllianceMember?.value
                )
            );
        }
    }, [
        facilityAllianceMemberIdNumber,
        facilityAllianceMember,
        setBreadcrumbs,
        FacilityAllianceMemberBreadcrumbs,
    ]);

    // Reload facility alliance member after saving
    useEffect(() => {
        if (
            !savingFacilityAllianceMembers &&
            saveFacilityAllianceMembersSuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Facility alliance member saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string =
                    "/settings/institution/facilityalliancemembers";
                navigate(url);
            } else if (!facilityAllianceMemberIdNumber) {
                let url: string = `/settings/institution/facilityalliancemembers/${facilityAllianceMember?.id}`;
                navigate(url);
                clearFacilityAllianceMembers();
            }
        }
    }, [
        savingFacilityAllianceMembers,
        saveFacilityAllianceMembersSuccess,
        facilityAllianceMemberIdNumber,
        navigate,
        clearFacilityAllianceMembers,
        saveAndReturn,
        facilityAllianceMember,
    ]);

    // Failed saving
    useEffect(() => {
        if (
            !savingFacilityAllianceMembers &&
            saveFacilityAllianceMembersFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingFacilityAllianceMembers, saveFacilityAllianceMembersFailure]);

    // Cleanup effect to clear the facility alliance member on component unmount
    useEffect(() => {
        return () => {
            clearFacilityAllianceMembers();
            resetForm(reduxFormName);
        };
    }, [clearFacilityAllianceMembers, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/institution/facilityalliancemembers";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.FacilityAllianceMember) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.FacilityAllianceMember =
                {} as Dtos.FacilityAllianceMember;

            await saveFacilityAllianceMembers(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.FacilityAllianceMember =
                {} as Dtos.FacilityAllianceMember;

            if (form && hasBeenSubmitted && !validatingFacilityAllianceMember) {
                await validateFacilityAllianceMember(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.FacilityAllianceMembersUpdate,
                Dtos.Permission.FacilityAllianceMembersCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingFacilityAllianceMembers ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "facilityAllianceMember",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "facilityAllianceMember",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "facilityAllianceMember",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "facilityAllianceMember",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingFacilityAllianceMembers,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <InstitutionLookupsLayout
            loading={loadingFacilityAllianceMembers}
            permission={Dtos.Permission.FacilityAllianceMembersView}
        >
            {renderContent}
        </InstitutionLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<FacilityAllianceMembersPageProps> => {
    let facilityAllianceMember: Dtos.FacilityAllianceMember | undefined = !(
        state.facilityAllianceMembers.formData instanceof Array
    )
        ? state.facilityAllianceMembers.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        facilityAllianceMember: facilityAllianceMember!,
        loadingFacilityAllianceMembers:
            state.facilityAllianceMembers.loadState &&
            state.facilityAllianceMembers.loadState.status ==
                RequestState.Pending,
        loadFacilityAllianceMembersFailure:
            state.facilityAllianceMembers.loadState &&
            state.facilityAllianceMembers.loadState.status ==
                RequestState.Failure,
        loadFacilityAllianceMembersErrorCode:
            state.facilityAllianceMembers.loadState.errorCode,
        savingFacilityAllianceMembers:
            state.facilityAllianceMembers.saveState &&
            state.facilityAllianceMembers.saveState.status ==
                RequestState.Pending,
        saveFacilityAllianceMembersSuccess:
            state.facilityAllianceMembers.saveState &&
            state.facilityAllianceMembers.saveState.status ==
                RequestState.Success,
        saveFacilityAllianceMembersFailure:
            state.facilityAllianceMembers.saveState &&
            state.facilityAllianceMembers.saveState.status ==
                RequestState.Failure,
        creatingFacilityAllianceMembers:
            state.facilityAllianceMembers.createState &&
            state.facilityAllianceMembers.createState.status ==
                RequestState.Pending,
        createFacilityAllianceMembersSuccess:
            state.facilityAllianceMembers.createState &&
            state.facilityAllianceMembers.createState.status ==
                RequestState.Success,
        validatingFacilityAllianceMember:
            state.facilityAllianceMembers.validateState &&
            state.facilityAllianceMembers.validateState.status ==
                RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.facilityAllianceMember,
        formState: state.facilityAllianceMembers.formState,
        formProperties: state.facilityAllianceMembers.formProperties,
        validationFailures: facilityAllianceMember
            ? state.facilityAllianceMembers.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<FacilityAllianceMembersPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadFacilityAllianceMembers: bindActionCreators(
            FacilityAllianceMembersActions.LoadFacilityAllianceMemberById,
            dispatch
        ),
        clearFacilityAllianceMembers: bindActionCreators(
            FacilityAllianceMembersActions.Clear,
            dispatch
        ),
        saveFacilityAllianceMembers: bindActionCreators(
            FacilityAllianceMembersActions.SaveFacilityAllianceMember,
            dispatch
        ),
        createFacilityAllianceMembers: bindActionCreators(
            FacilityAllianceMembersActions.CreateFacilityAllianceMember,
            dispatch
        ),
        validateFacilityAllianceMember: bindActionCreators(
            FacilityAllianceMembersActions.ValidateFacilityAllianceMember,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(FacilityAllianceMembersPage);
